import React, { useMemo, useRef, useEffect, useContext } from 'react'
import { usePaginationFragment, graphql } from 'react-relay'

import { Grid } from '@newComponents/Grid'
import { Button } from '@newComponents/Button/Button'
import { YStack, StackProps } from 'tamagui'

import { PresentationCard } from '@newComponents/Presentation/PresentationCard'

import {
  PresentationsScreenListSection_query$key,
  PresentationsScreenListSection_query$data,
} from './__generated__/PresentationsScreenListSection_query.graphql'

import { Spinner } from '@newComponents/Spinner'
import { ServerContext } from '@data/ServerContext'

type PresentationEdge = NonNullable<
  PresentationsScreenListSection_query$data['presentations']
>['edges'][number]

const groupPresentations = (
  presentations: readonly PresentationEdge[],
  itemsPerRow: number
): PresentationEdge[][] => {
  return presentations.reduce<PresentationEdge[][]>(
    (memo, presentation, index) => {
      if (index % itemsPerRow === 0) {
        memo.push([presentation])
      } else {
        memo[memo.length - 1].push(presentation)
      }
      return memo
    },
    []
  )
}

const PresentationCardList = ({
  presentations,
  itemsPerRow,
  hasNext,
  loadNext,
  isLoadingNext,
  ...props
}: {
  presentations: NonNullable<
    PresentationsScreenListSection_query$data['presentations']
  >['edges']
  itemsPerRow: number
  hasNext: boolean
  loadNext: (count: number) => any
  isLoadingNext: boolean
} & StackProps) => {
  const groups = useMemo(
    () => groupPresentations(presentations, itemsPerRow),
    [presentations, itemsPerRow]
  )

  const endOfListElement = useRef(null)
  const serverContext = useContext(ServerContext)

  useEffect(() => {
    const currentEndOfListElement = endOfListElement.current

    if (
      !currentEndOfListElement ||
      !hasNext ||
      serverContext ||
      typeof window === 'undefined' ||
      !window.IntersectionObserver
    ) {
      return
    }

    const intersectionObserver = new window.IntersectionObserver((entries) => {
      const someIntersecting = entries.some((entry) => entry.isIntersecting)

      if (someIntersecting) {
        loadNext(12)
      }
    })

    intersectionObserver.observe(currentEndOfListElement)

    return () => {
      intersectionObserver.disconnect()
    }
  }, [endOfListElement.current, hasNext, loadNext])

  return (
    <YStack gap='$6' {...props}>
      <YStack gap='$4'>
        {groups.map((group, index) => (
          <YStack key={index}>
            <Grid gap='$4'>
              {group.map((edge) => (
                <Grid.Column key={edge.cursor}>
                  <PresentationCard
                    presentation={edge.node}
                    titleNumberOfLines={2}
                    numberOfLines={8}
                    flex={1}
                  />
                </Grid.Column>
              ))}
              {Array.from({ length: itemsPerRow - group.length }).map(
                (_, i) => (
                  <Grid.Column key={i} />
                )
              )}
            </Grid>
          </YStack>
        ))}
      </YStack>

      {hasNext ? (
        <Spinner ref={endOfListElement} size='$9' color='$primaryColor' />
      ) : null}
    </YStack>
  )
}

export const PresentationsScreenListSection = ({
  query,
}: {
  query: PresentationsScreenListSection_query$key
}) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment PresentationsScreenListSection_query on Query
      @refetchable(queryName: "PresentationsScreenListSectionPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 12 }
        cursor: { type: "Cursor" }
      ) {
        presentations(
          first: $count
          after: $cursor
          orderBy: [CREATED_AT_DESC, ID_ASC]
        )
          @connection(
            key: "PresentationsScreenListSection_query_presentations"
          ) {
          edges {
            node {
              ...PresentationCardNew_presentation
            }
            cursor
          }
        }
      }
    `,
    query
  )

  if (!data.presentations?.edges) {
    console.error('No presentations found')
    return null
  }

  return (
    <>
      <PresentationCardList
        presentations={data.presentations.edges}
        itemsPerRow={3}
        hasNext={hasNext}
        loadNext={loadNext}
        isLoadingNext={isLoadingNext}
        display='none'
        $gtMd={{ display: 'flex' }}
      />

      <PresentationCardList
        presentations={data.presentations.edges}
        itemsPerRow={2}
        hasNext={hasNext}
        loadNext={loadNext}
        isLoadingNext={isLoadingNext}
        display='none'
        $gtXs={{ display: 'flex' }}
        $gtMd={{ display: 'none' }}
      />

      <PresentationCardList
        presentations={data.presentations.edges}
        itemsPerRow={1} // Small screens
        hasNext={hasNext}
        loadNext={loadNext}
        isLoadingNext={isLoadingNext}
        display='flex'
        $gtXs={{ display: 'none' }}
      />
    </>
  )
}
